import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import './AdminReport.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const AdminReport = ({ user }) => {
  const [activeTab, setActiveTab] = useState('pageTime');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pageTimeData, setPageTimeData] = useState([]);
  const [userLoginData, setUserLoginData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const [pageRes, userRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/report/page-time`, { params, withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/report/user-logins`, { params, withCredentials: true })
      ]);

      if (pageRes.data.success) setPageTimeData(pageRes.data.data);
      if (userRes.data.success) setUserLoginData(userRes.data.data);
    } catch (err) {
      console.error('Report fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const formatDate = (val) => val ? String(val).split('T')[0] : '';

  // Compute totals
  const totalTimeSeconds = pageTimeData.reduce((sum, r) => sum + Number(r.total_seconds || 0), 0);
  const totalTimeMinutes = Math.round(totalTimeSeconds / 60 * 10) / 10;
  const totalUsers = userLoginData.length;

  const handleExport = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Page Time Spent
    const pageHeaders = ['Page URL', 'Unique Users', 'Total Visits', 'Total Seconds', 'Total Minutes'];
    const pageRows = pageTimeData.map(r => [r.page_url, r.unique_users, r.total_visits, r.total_seconds, r.total_minutes]);
    pageRows.push([]);
    pageRows.push(['Total Time on Portal', '', '', totalTimeSeconds, totalTimeMinutes]);
    pageRows.push(['Total Logged-in Users', '', '', '', totalUsers]);
    const ws1 = XLSX.utils.aoa_to_sheet([pageHeaders, ...pageRows]);
    XLSX.utils.book_append_sheet(wb, ws1, 'Per Page Time Spent');

    // Sheet 2: Unique Users
    const userHeaders = ['Enterprise ID', 'Total Logins', 'Days Active', 'First Login', 'Last Login'];
    const userRows = userLoginData.map(r => [r.enterprise_id, r.total_logins, r.days_active, formatDate(r.first_login), formatDate(r.last_login)]);
    userRows.push([]);
    userRows.push(['Total Logged-in Users', totalUsers, '', '', '']);
    const ws2 = XLSX.utils.aoa_to_sheet([userHeaders, ...userRows]);
    XLSX.utils.book_append_sheet(wb, ws2, 'Unique Users');

    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `LB-UserReport${today}.xlsx`);
  };

  const handleNavigation = () => {};

  return (
    <div className="admin-report">
      <Header user={user} />
      <SideNav onNavigate={handleNavigation} />
      <main className="admin-report-content">
        <h1 className="admin-report-title">Admin Report</h1>

        <form className="admin-report-filters" onSubmit={handleSubmit}>
          <div className="admin-filter-group">
            <label>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="admin-filter-group">
            <label>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <button type="submit" className="admin-submit-btn">Submit</button>
          <button type="button" className="admin-download-btn" onClick={handleExport}>
            Export
          </button>
        </form>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'pageTime' ? 'active' : ''}`}
            onClick={() => setActiveTab('pageTime')}
          >
            Per Page Time Spent
          </button>
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Unique Users
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading...</div>
        ) : activeTab === 'pageTime' ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Page URL</th>
                  <th>Unique Users</th>
                  <th>Total Visits</th>
                  <th>Total Seconds</th>
                  <th>Total Minutes</th>
                </tr>
              </thead>
              <tbody>
                {pageTimeData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.page_url}</td>
                    <td>{row.unique_users}</td>
                    <td>{row.total_visits}</td>
                    <td>{row.total_seconds}</td>
                    <td>{row.total_minutes}</td>
                  </tr>
                ))}
                {pageTimeData.length === 0 && (
                  <tr><td colSpan="5" className="admin-no-data">No data found</td></tr>
                )}
                {pageTimeData.length > 0 && (
                  <>
                    <tr className="admin-total-row">
                      <td>Total Time on Portal</td>
                      <td></td>
                      <td></td>
                      <td>{totalTimeSeconds}</td>
                      <td>{totalTimeMinutes}</td>
                    </tr>
                    <tr className="admin-total-row">
                      <td>Total Logged-in Users</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>{totalUsers}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Enterprise ID</th>
                  <th>Total Logins</th>
                  <th>Days Active</th>
                  <th>First Login</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {userLoginData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.enterprise_id}</td>
                    <td>{row.total_logins}</td>
                    <td>{row.days_active}</td>
                    <td>{row.first_login ? row.first_login.split('T')[0] : ''}</td>
                    <td>{row.last_login ? row.last_login.split('T')[0] : ''}</td>
                  </tr>
                ))}
                {userLoginData.length === 0 && (
                  <tr><td colSpan="5" className="admin-no-data">No data found</td></tr>
                )}
                {userLoginData.length > 0 && (
                  <tr className="admin-total-row">
                    <td>Total Logged-in Users</td>
                    <td>{totalUsers}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AdminReport;
